/**
 * Shared quote PDF body HTML — same layout as vacation-package (חבילות נופש) PDFs.
 * Used by PackageDetails-style exports, Belvedere campaign, and HotelsRiver DP.
 */
import {
  formatVpPdfPriceIls,
  formatVpPdfPriceWithUsdParenthetical,
} from "./resolveVpPdfTotalPrice";

export function escapeQuotePdfHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * @param {object} opts
 * @returns {string}
 */
export function buildVacationPackageQuoteBodyHtml({
  logoBase64,
  quoteRefLine = "",
  titleHe = "הצעת מחיר - חבילת נופש",
  hotelName,
  boardDetails,
  hotelCity,
  depDate,
  retDate,
  roomsInfo = [],
  flightInfo = [],
  supplementsList = [],
  totalPrice,
  ilsToUsd = null,
  includeAgentSectionInPdf = false,
  agentCompany = "",
  agentName = "",
  agentPhone = "",
  agentEmail = "",
} = {}) {
  const escapeHtml = escapeQuotePdfHtml;
  const priceDisplay = formatVpPdfPriceWithUsdParenthetical(totalPrice, ilsToUsd);

  return `
        <div class="header">
          <img src="${logoBase64}" alt="HotelsRiver" class="logo" loading="eager" style="width: auto; height: auto; max-width: 180px; max-height: 60px; object-fit: contain;">
          <h1>${escapeHtml(titleHe)}</h1>
          ${quoteRefLine}
        </div>
        <div class="content">
          ${hotelName ? `
            <div class="section">
              <div class="info-section">
                <div class="info-section-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: inline-block; vertical-align: middle; margin-left: 5px;">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                  פרטי המלון
                </div>
                <div class="card">
                  <div class="info-row">
                    <span class="info-label">מלון:</span>
                    <span class="info-value"><strong style="font-weight: bold; font-size: 1.1em;">${escapeHtml(hotelName)}</strong></span>
                  </div>
                  ${boardDetails ? `
                    <div class="info-row">
                      <span class="info-label">בסיס אירוח:</span>
                      <span class="info-value"><strong style="font-weight: bold; font-size: 1.1em;">${escapeHtml(boardDetails)}</strong></span>
                    </div>
                  ` : ""}
                  ${hotelCity ? `
                    <div class="info-row">
                      <span class="info-label">מיקום:</span>
                      <span class="info-value">${escapeHtml(hotelCity)}</span>
                    </div>
                  ` : ""}
                  ${depDate && retDate ? `
                    <div class="dates">
                      <div class="date-box">
                        <div class="date-label">תאריך יציאה</div>
                        <div class="date-value">${escapeHtml(depDate)}</div>
                      </div>
                      <div class="date-box">
                        <div class="date-label">תאריך חזרה</div>
                        <div class="date-value">${escapeHtml(retDate)}</div>
                      </div>
                    </div>
                  ` : ""}
                </div>
              </div>
            </div>
          ` : ""}

          ${roomsInfo.length > 0 ? `
            <div class="section">
              <div class="info-section">
                <div class="info-section-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: inline-block; vertical-align: middle; margin-left: 5px;">
                    <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                    <line x1="3" y1="14" x2="21" y2="14"></line>
                  </svg>
                  פרטי חדרים
                </div>
                ${roomsInfo.map((room, idx) => `
                  <div class="card">
                    <div class="info-row">
                      <span class="info-label">חדר ${idx + 1}:</span>
                      <span class="info-value"><strong style="font-weight: bold; font-size: 1.1em;">${escapeHtml(room.roomName)}</strong></span>
                    </div>
                    ${room.board ? `
                      <div class="info-row">
                        <span class="info-label">בסיס אירוח:</span>
                        <span class="info-value"><strong style="font-weight: bold; font-size: 1.1em;">${escapeHtml(room.board)}</strong></span>
                      </div>
                    ` : ""}
                    ${room.occupancy ? `
                      <div class="info-row">
                        <span class="info-label">תפוסה:</span>
                        <span class="info-value">${escapeHtml(room.occupancy)}</span>
                      </div>
                    ` : ""}
                    ${room.displayPriceIls != null ? `
                      <div class="info-row">
                        ${room.priceLabelHe
                          ? `<span class="info-label">${escapeHtml(room.priceLabelHe)}:</span>`
                          : ""}
                        <span class="info-value"><strong>${formatVpPdfPriceIls(room.displayPriceIls)}</strong></span>
                      </div>
                    ` : ""}
                  </div>
                `).join("")}
              </div>
            </div>
          ` : ""}

          ${flightInfo.length > 0 ? `
            <div class="section">
              <div class="info-section">
                <div class="info-section-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: inline-block; vertical-align: middle; margin-left: 5px;">
                    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"></path>
                  </svg>
                  פרטי טיסות
                </div>
                ${flightInfo.map((flight) => `
                  <div class="card">
                    <div class="info-row">
                      <span class="info-label">${escapeHtml(flight.type)}:</span>
                      <span class="info-value">${escapeHtml(flight.depCity || flight.depAirport)} → ${escapeHtml(flight.arrCity || flight.arrAirport)}</span>
                    </div>
                    ${flight.airlineName ? `
                      <div class="info-row" style="display: flex; align-items: center; gap: 4px;">
                        <span class="info-label">חברת תעופה:</span>
                        <span class="info-value" style="display: flex; align-items: center; gap: 4px;">
                          ${flight.airlineLogo ? `<img src="${escapeHtml(flight.airlineLogo)}" alt="${escapeHtml(flight.airlineName)}" style="height: 40px; min-height: 40px; max-height: 50px; width: auto; max-width: 100px; min-width: 80px; object-fit: contain; display: block; padding: 4px; background: #ffffff; border-radius: 6px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);" loading="eager" crossorigin="anonymous" onerror="this.style.display='none';" />` : ""}
                          <strong>${escapeHtml(flight.airlineName)}</strong>
                        </span>
                      </div>
                    ` : ""}
                    ${flight.flightNumber ? `
                      <div class="info-row">
                        <span class="info-label">מספר טיסה:</span>
                        <span class="info-value">${escapeHtml(`${flight.airline} ${flight.flightNumber}`.trim())}</span>
                      </div>
                    ` : ""}
                    ${flight.depDate ? `
                      <div class="info-row">
                        <span class="info-label">תאריך המראה:</span>
                        <span class="info-value">${escapeHtml(flight.depDate)}</span>
                      </div>
                    ` : ""}
                    ${flight.depTime ? `
                      <div class="info-row">
                        <span class="info-label">שעת המראה:</span>
                        <span class="info-value"><strong>${escapeHtml(flight.depTime)}</strong></span>
                      </div>
                    ` : ""}
                    ${flight.arrDate ? `
                      <div class="info-row">
                        <span class="info-label">תאריך נחיתה:</span>
                        <span class="info-value">${escapeHtml(flight.arrDate)}</span>
                      </div>
                    ` : ""}
                    ${flight.arrTime ? `
                      <div class="info-row">
                        <span class="info-label">שעת נחיתה:</span>
                        <span class="info-value"><strong>${escapeHtml(flight.arrTime)}</strong></span>
                      </div>
                    ` : ""}
                    ${flight.baggageLabel ? `
                      <div class="info-row">
                        <span class="info-label">כבודה:</span>
                        <span class="info-value">${escapeHtml(flight.baggageLabel)}</span>
                      </div>
                    ` : ""}
                  </div>
                `).join("")}
              </div>
            </div>
          ` : ""}

          ${supplementsList.length > 0 ? `
            <div class="section">
              <div class="info-section">
                <div class="info-section-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: inline-block; vertical-align: middle; margin-left: 5px;">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="16"></line>
                    <line x1="8" y1="12" x2="16" y2="12"></line>
                  </svg>
                  תוספות
                </div>
                ${supplementsList.map((supp) => `
                  <div class="card">
                    <div class="info-row">
                      <span class="info-label">${escapeHtml(supp.name)}:</span>
                      <span class="info-value"><strong>${formatVpPdfPriceIls(supp.priceIls)}</strong></span>
                    </div>
                  </div>
                `).join("")}
              </div>
            </div>
          ` : ""}

          <div class="section">
            <div class="info-section">
              <div class="info-section-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: inline-block; vertical-align: middle; margin-left: 5px;">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                סיכום תשלום
              </div>
              <div class="price-highlight">
                <div class="info-row">
                  <span class="info-label">סכום לתשלום (הערכה):</span>
                  <span class="info-value">${priceDisplay}</span>
                </div>
                <div style="font-size: 8pt; color: rgba(255,255,255,0.9); margin-top: 4px; text-align: right; line-height: 1.3;">
                  הסכום הסופי ייקבע לפי שער המחאות נוסעים ביום התשלום
                </div>
              </div>
            </div>
          </div>

          ${includeAgentSectionInPdf ? `
            <div class="section">
              <div class="info-section">
                <div class="info-section-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: inline-block; vertical-align: middle; margin-left: 5px;">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  פרטי הסוכן
                </div>
                <div class="card">
                  ${agentCompany ? `
                    <div class="info-row">
                      <span class="info-label">סוכנות:</span>
                      <span class="info-value">${escapeHtml(agentCompany)}</span>
                    </div>
                  ` : ""}
                  ${agentName ? `
                    <div class="info-row">
                      <span class="info-label">שם הסוכן:</span>
                      <span class="info-value">${escapeHtml(agentName)}</span>
                    </div>
                  ` : ""}
                  ${agentPhone ? `
                    <div class="info-row">
                      <span class="info-label">טלפון:</span>
                      <span class="info-value">${escapeHtml(agentPhone)}</span>
                    </div>
                  ` : ""}
                  ${agentEmail ? `
                    <div class="info-row">
                      <span class="info-label">מייל:</span>
                      <span class="info-value">${escapeHtml(agentEmail)}</span>
                    </div>
                  ` : ""}
                </div>
              </div>
            </div>
          ` : ""}

          <div class="section">
            <div class="terms-section">
              <div class="terms-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: inline-block; vertical-align: middle; margin-left: 5px;">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                מדיניות ביטול ושינוי הזמנות
              </div>
              <div class="terms-content">
                <p>מדיניות הביטול והשינוי שיחולו על ההזמנות יהיו בהתאם לתעריפי הספקים השונים (בתי המלון, חברות התעופה וכיו"ב), וישתנו משירות לשירות, כמצוין בפרטי ההזמנה, ובכפוף להוראות חוק הגנת הצרכן, התשמ"א-1981.</p>
                <p><strong>חשוב לדעת:</strong> שינוי הזמנה או ביטול הזמנה יהיו כרוכים בדמי טיפול של 85$ עבור כל אדם. יובהר כי דמי הטיפול הינם בנוסף לדמי הביטול ולדמי השינוי שנקבעו בהתאם למדיניות הביטול והשינוי של הספקים.</p>
                <p>לתקנון המלא: <a href="https://www.hotelsriver.com/termsAndConditions" style="color:#0078D4;text-decoration:underline;">https://www.hotelsriver.com/termsAndConditions</a></p>
              </div>
              <div class="terms-title" style="margin-top: 8px;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: inline-block; vertical-align: middle; margin-left: 5px;">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                  <line x1="1" y1="10" x2="23" y2="10"></line>
                </svg>
                תנאי תשלום
              </div>
              <div class="terms-content">
                <p><strong>חשוב:</strong> יש לשלם עבור ההזמנה טרם הגעת תנאי הביטול. אי תשלום יגרור ביטול אוטומטי של ההזמנה ללא אפשרות חידוש.</p>
                <p>מחירים, זמינות ותנאי רכישה אחרים מופיעים בדפי האתר ועשויים להשתנות מעת לעת. האתר שומר לעצמו את הזכות לשנות את מגוון השירותים או להפסיקם או לשנות את מחיר השירותים המוצעים באתר ומחוצה לו, ללא הודעה מוקדמת.</p>
                <p><strong>שער החליפין:</strong> החיובים בגין רכישות באתר יבוצעו בשקלים חדשים בהתאם לשער ההעברות וההמחאות (מכירה) כפי שפורסם ביום העסקים שקדם ליום ביצוע ההזמנה.</p>
                <p>תשלום סופי ייקבע על פי השער בעת התשלום בפועל</p>
              </div>
            </div>
          </div>

        </div>
      `;
}
